import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Star, Zap, Package, TrendingUp, MapPin, Crown, Flame, Trophy, ArrowLeft, Gift } from "lucide-react";
import ShareCard from "@/components/ShareCard";
import { logger } from "@/lib/logger";

const REFERRAL_KEY = "rivendas_referrer";

export default function PublicProfile() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser(data);
        // Save referrer for potential signup
        try {
          localStorage.setItem(
            REFERRAL_KEY,
            JSON.stringify({ user_id: userId, name: data.name, ts: Date.now() })
          );
        } catch (storageErr) {
          logger.warn("referral: unable to persist", storageErr?.message);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          logger.error("public profile: failed to load", err);
        }
      }
      setLoading(false);
    })();
  }, [userId]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregando...</div>;
  }
  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-muted-foreground p-4">
        <p>Perfil não encontrado</p>
        <Link to="/" className="text-primary underline mt-2">Voltar</Link>
      </div>
    );
  }

  const formatResp = (s) => (s == null ? "—" : s < 60 ? `${Math.round(s)}s` : `${Math.round(s / 60)}min`);

  return (
    <div className="min-h-screen bg-background">
      {/* Simple header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 h-14">
          <Link to="/" className="flex items-center gap-2" data-testid="public-brand-link">
            <span className="inline-block w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-heading font-black text-lg">R</span>
            </span>
            <span className="font-heading font-black">
              <span className="text-primary">Ri</span>vendas
            </span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft size={14} /> Ir ao app
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="public-profile">
        {user.weekly_position && (
          <div className="mb-6 inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-bold">
            <Trophy size={14} /> #{user.weekly_position} no ranking desta semana
          </div>
        )}

        <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center font-heading font-black text-3xl">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight" data-testid="public-name">
                {user.name}
              </h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs text-muted-foreground capitalize">{user.role}</span>
                {user.city && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin size={11} /> {user.city}
                  </span>
                )}
                {user.is_fast_seller && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded-full">
                    <Zap size={10} strokeWidth={3} /> Vendedor Rápido
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            <div className="bg-secondary rounded-xl p-4 text-center">
              <Star size={16} className="text-amber-500 mx-auto mb-1" />
              <p className="font-heading font-black text-2xl">
                {user.rating_avg > 0 ? user.rating_avg.toFixed(1) : "—"}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{user.rating_count} avaliações</p>
            </div>
            <div className="bg-secondary rounded-xl p-4 text-center">
              <Package size={16} className="text-primary mx-auto mb-1" />
              <p className="font-heading font-black text-2xl">{user.total_sales || 0}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Vendas totais</p>
            </div>
            <div className="bg-secondary rounded-xl p-4 text-center">
              <TrendingUp size={16} className="text-emerald-500 mx-auto mb-1" />
              <p className="font-heading font-black text-2xl">{user.weekly_sales || 0}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Vendas / 7d</p>
            </div>
            <div className="bg-secondary rounded-xl p-4 text-center">
              <Zap size={16} className="text-primary mx-auto mb-1" />
              <p className="font-heading font-black text-2xl">{formatResp(user.avg_response_seconds)}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Resposta</p>
            </div>
          </div>

          {(user.fast_accepts_this_week > 0 || user.top10_streak > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {user.fast_accepts_this_week > 0 && (
                <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/10 border border-orange-500/30 rounded-xl p-4 flex items-center gap-3">
                  <Flame size={26} className="text-orange-400 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-orange-400 font-bold">Streak semanal</p>
                    <p className="font-heading font-black text-2xl">{user.fast_accepts_this_week}</p>
                  </div>
                </div>
              )}
              {user.top10_streak > 0 && (
                <div className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded-xl p-4 flex items-center gap-3">
                  <Crown size={26} className="text-primary shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-primary font-bold">Top 10 semanal</p>
                    <p className="font-heading font-black text-2xl">
                      {user.top10_streak}× <span className="text-xs text-muted-foreground font-normal">seguidas</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            data-testid="public-share-btn"
            onClick={() => setShareOpen(true)}
            className="w-full mt-6 h-12 rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90"
          >
            Compartilhar este perfil
          </button>
        </div>

        <div className="mt-8 bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded-2xl p-5 text-center" data-testid="referral-cta">
          <Gift size={28} className="text-primary mx-auto mb-2" />
          <h3 className="font-heading font-black text-xl">
            🎁 Você foi convidado(a) por {user.name?.split(" ")[0]}
          </h3>
          <p className="text-sm text-muted-foreground mt-1 mb-3">
            Crie sua conta agora e ganhe seu <strong className="text-primary">1º pedido em destaque grátis</strong> — vendedores respondem primeiro a você.
          </p>
          <Link
            to="/register?role=buyer"
            data-testid="referral-register-btn"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90"
          >
            Criar minha conta grátis →
          </Link>
        </div>
      </div>

      <ShareCard open={shareOpen} onClose={() => setShareOpen(false)} user={user} appUrl={appUrl} />
    </div>
  );
}
